import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StarMark } from "@/components/Logo";
import { Accessibility } from "lucide-react";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";

const NAV = [
  { to: "/how-it-works", label: "How it works" },
  { to: "/brief", label: "White papers" },
  { to: "/maturity-assessment", label: "PMO maturity" },
  { to: "/risk-health-check", label: "Risk health check" },
  { to: "/plan-health-check", label: "Plan health check" },
  { to: "/supplier-risk-lens", label: "Supplier risk lens" },
  { to: "/trust", label: "Trust Centre" },
  { to: "/catalogue", label: "Connector catalogue" },
  { to: "/roadmap", label: "Roadmap" },
  { to: "/demo", label: "Book a demo" },
  { to: "/pilot", label: "Book a pilot" },
];

export function PublicShell({ children, active }) {
  const { t: tFn } = useTranslation();
  return (
    <div className="min-h-screen overflow-x-hidden bg-ek-canvas text-ek-ink">
      {/* Header — mirrors ekselens.com: black bar, white text, primary CTA in
          purple on the right, sign-in as a bordered button next to it. */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-black">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link to="/how-it-works" className="flex items-center gap-2.5" data-testid="public-home-link">
            <StarMark size={28} className="text-white" />
            <span className="font-display text-sm font-semibold tracking-[-0.01em] text-white">
              EKSELENS <span className="font-light text-white/55">· ERIO</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-5 md:flex">
            {NAV.filter((n) => !["/demo", "/pilot"].includes(n.to)).map((n) => (
              <Link key={n.to} to={n.to}
                className={`text-[13px] transition-colors hover:text-white ${active === n.to ? "font-semibold text-white" : "font-light text-white/65"}`}
                data-testid={`nav-${n.to.slice(1)}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {/* LocaleSwitcher hidden below `sm` so the CTA row fits a 375px viewport. */}
            <div className="hidden sm:inline-flex">
              <LocaleSwitcher tone="dark" />
            </div>
            <Link to="/login"
              className="hidden rounded-md border border-white/20 px-3 py-1.5 text-[13px] font-medium text-white/85 transition-colors hover:border-white hover:text-white sm:inline-block"
              data-testid="nav-signin"
            >
              {tFn("common.signIn")}
            </Link>
            <Link to="/demo"
              className="rounded-md bg-ek-purple px-3.5 py-1.5 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-ek-purple/90"
              data-testid="nav-book-demo"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </header>
      <main>{children}</main>
      <footer className="border-t border-black/5 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-8 text-xs text-ek-ink/70">
          <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center" data-testid="footer-newsletter">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-ek-purple font-semibold">Stay in the loop</p>
              <p className="mt-1 text-ek-ink/70">
                One list shared with ekselens.com — quarterly roundup, no spam.
              </p>
            </div>
            <NewsletterSignup source="public-footer" />
          </div>
          <div className="flex flex-col items-center justify-between gap-3 border-t border-black/5 pt-4 sm:flex-row">
            <span>© {new Date().getFullYear()} Ekselens Consulting · ERIO. Commercial-in-Confidence. Patent filed GB2613313.2</span>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/trust#trust-accessibility" aria-label="Accessibility — WCAG 2.1 AA conformance, see Trust Centre"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-ek-canvas px-2.5 py-1 font-medium text-ek-ink/70 transition-colors hover:border-ek-purple/40 hover:text-ek-purple"
                data-testid="footer-a11y-badge">
                <Accessibility size={13} /> WCAG 2.1 AA
              </Link>
              <Link to="/brief" className="hover:text-ek-purple">White papers</Link>
              <Link to="/trust" className="hover:text-ek-purple">Trust Centre</Link>
              <Link to="/status" className="hover:text-ek-purple" data-testid="footer-status-link">Status</Link>
              <Link to="/changelog" className="hover:text-ek-purple" data-testid="footer-changelog-link">Changelog</Link>
              <Link to="/benchmarks" className="hover:text-ek-purple" data-testid="footer-benchmarks-link">Benchmarks</Link>
              <Link to="/privacy" className="hover:text-ek-purple">Privacy</Link>
              <Link to="/terms" className="hover:text-ek-purple" data-testid="footer-terms-link">Terms</Link>
              <a href="mailto:info@ekselens.com" className="hover:text-ek-purple">info@ekselens.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
